/**
 * useDiscoverySources Hooks
 * Phase 4: Training Provider Expansion Framework
 * Manage training_sources (discovery feeds) for each provider.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route discovery-source `disabled_at` write through clock.
import { clock } from '@/utils/clock';

export interface TrainingSource {
  id: string;
  provider_id: string;
  source_type: string;
  source_identifier: string;
  source_name: string;
  is_active: boolean;
  last_fetched_at: string | null;
  last_fetch_status: string | null;
  last_fetch_error: string | null;
  fetch_count: number | null;
  ttl_days: number;
  max_items_per_fetch: number;
  created_at: string;
  updated_at: string;
  disabled_at: string | null;
  disabled_by: string | null;
  // Joined provider info
  provider?: {
    id: string;
    display_name: string;
  } | null;
}

/**
 * Fetch all training sources with provider join
 */
export function useDiscoverySources(providerId?: string, includeDisabled = false) {
  return useQuery({
    queryKey: ['training-sources', providerId, includeDisabled],
    queryFn: async () => {
      let query = supabase
        .from('training_sources')
        .select(`
          *,
          provider:training_providers(id, display_name)
        `)
        .order('created_at', { ascending: false });

      if (providerId) {
        query = query.eq('provider_id', providerId);
      }

      if (!includeDisabled) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as TrainingSource[];
    },
  });
}

/**
 * Update source active status (enable/disable)
 */
export function useUpdateSourceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, isActive }: { sourceId: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('training_sources')
        .update({ is_active: isActive })
        .eq('id', sourceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
    },
  });
}

/**
 * Create a new training source
 */
export function useCreateSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (source: {
      provider_id: string;
      source_type: string;
      source_identifier: string;
      source_name: string;
      ttl_days?: number;
      max_items_per_fetch?: number;
    }) => {
      const { data, error } = await supabase
        .from('training_sources')
        .insert({
          provider_id: source.provider_id,
          source_type: source.source_type,
          source_identifier: source.source_identifier,
          source_name: source.source_name,
          ttl_days: source.ttl_days ?? 7,
          max_items_per_fetch: source.max_items_per_fetch ?? 50,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
    },
  });
}

/**
 * Update a training source (all editable fields)
 */
export function useUpdateSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      sourceId, 
      updates 
    }: { 
      sourceId: string; 
      updates: {
        source_name?: string;
        source_identifier?: string;
        source_type?: string;
        ttl_days?: number;
        max_items_per_fetch?: number;
        is_active?: boolean;
      };
    }) => {
      const { error } = await supabase
        .from('training_sources')
        .update(updates)
        .eq('id', sourceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
    },
  });
}

/**
 * Soft delete a training source (set is_active=false, disabled_at, disabled_by)
 */
export function useDisableSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, userId }: { sourceId: string; userId?: string }) => {
      const { error } = await supabase
        .from('training_sources')
        .update({
          is_active: false,
          disabled_at: clock.now().toISOString(),
          disabled_by: userId || null,
        })
        .eq('id', sourceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
    },
  });
}

/**
 * Run discovery now for a specific source or provider
 */
export function useRunDiscoveryNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      sourceId, 
      providerId 
    }: { 
      sourceId?: string; 
      providerId?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('fetch-training-sources', {
        body: {
          source_id: sourceId,
          provider_id: providerId,
          trigger: 'manual',
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sources'] });
      queryClient.invalidateQueries({ queryKey: ['pending-discoveries'] });
      queryClient.invalidateQueries({ queryKey: ['discovery-summary'] });
    },
  });
}
